import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import toast from "react-hot-toast";
import { BaseUrl } from "../../BaseUrl";
import { useAuth } from "../../hooks/useAuth";

const LoginHfr = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginWithOtp, setLoginWithOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const sendOtp = async () => {
    const emailOrMobile = getValues("email");
    if (!emailOrMobile) {
      toast.error("Please enter email or mobile number");
      return;
    }
    try {
      const response = await fetch(`${BaseUrl}/doctor/sendOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emailOrMobile }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send OTP");
      }

      toast.success("OTP sent successfully!");

      if (otpTimer > 0) return;
      setOtpSent(true);
      setOtpTimer(30);
      startOtpCountdown();
    } catch (error) {
      console.error("OTP Sending Failed:", error);
      toast.error("Failed to send OTP. Please try again.");
      setOtpSent(true);
    }
  };

  const verifyOtpAndLogin = async () => {
    const emailOrMobile = getValues("email");
    const otp = getValues("otp");

    if (!emailOrMobile || !otp) {
      toast.error("Please enter email/mobile and OTP");
      return;
    }

    try {
      const response = await fetch(`${BaseUrl}/doctor/verifyOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emailOrMobile, otp }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "OTP verification failed");
      }

      toast.success("OTP verified successfully!");

      // Call the login function after successful OTP verification
      await handleLogin({ emailOrMobile, otp });
    } catch (error) {
      console.error("OTP Verification Failed:", error);
      toast.error("Invalid OTP. Please try again.");
    }
  };
  const handleLogin = async (payload) => {
    try {
      const loginResponse = await fetch(`${BaseUrl}/doctor/doctorLogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const loginData = await loginResponse.json();

      if (!loginResponse.ok) {
        throw new Error(loginData.message || "Login failed");
      }

      console.log("Login Data:", loginData);

      localStorage.setItem("token", loginData.data.token);
      localStorage.setItem("email", loginData.data.emailId);
      localStorage.setItem("id", loginData.data._id);

      await fetchUser();

      toast.success("Login successfully!");

      const profileResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/doctor/getAllDoctorProfile`
      );

      if (!profileResponse.ok) {
        throw new Error("Failed to fetch doctor profiles");
      }

      const profileData = await profileResponse.json();
      const doctorData = profileData?.data?.data;

      const docId = doctorData.find(
        (item) => item.emailId === loginData.data.emailId
      );

      if (payload.emailOrMobile === "it@avijo.in") {
        navigate("/profile");
        return;
      }

      navigate(`/doctors/preview/${docId._id}`);
    } catch (error) {
      console.error("Login Failed:", error);
      throw error;
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    if (loginWithOtp) {
      await verifyOtpAndLogin(); // Verify OTP first and then log in
    } else {
      const payload = { emailOrMobile: data.email, password: data.password };
      try {
        await handleLogin(payload);
      } catch (error) {
        toast.error("Please Make an Account");
        if (error.message === "User not found") {
          navigate("/hfrlogin");
        }
      }
    }
  };

  const startOtpCountdown = () => {
    const timerInterval = setInterval(() => {
      setOtpTimer((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerInterval);
          setOtpSent(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  return (
    <section className='flex items-center justify-center bg-gray-100 p-4'>
      <div className='flex w-full max-w-6xl flex-col rounded-lg border bg-white p-9 shadow-lg lg:max-w-6xl lg:flex-row'>
        {/* Left Section */}
        <div className='w-full pr-6 lg:w-2/5'>
          <h2 className='mb-4 text-2xl font-semibold text-gray-700'>
            Keep track of your patient's health
          </h2>
          <p className='mb-4 text-gray-600'>
            with this powerful app that makes it easy to communicate with them.
          </p>

          <ul className='list-inside list-disc space-y-2 text-gray-700'>
            <li>Create a professional profile</li>
            <li>Set available timings</li>
            <li>Accept/Reject appointments</li>
            <li>Real-time chat with patients</li>
            <li>Read patient reviews</li>
            <li>Check customer reviews</li>
            <li>Easy login with OTP verification</li>
            <li>Multi-Lingual support including RTL</li>
          </ul>
        </div>

        {/* Right Section */}
        <div className='w-full border-t pl-6 lg:w-3/5 lg:border-l lg:border-t-0'>
          <h2 className='mb-4 text-center text-2xl font-semibold text-gray-700'>
            Login
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
            <div>
              <label className='block font-medium text-gray-700'>
                Mobile Number / HF ID / Email ID
              </label>
              <input
                type='text'
                {...register("email", {
                  required: "Email or mobile is required",
                })}
                className='mt-1 w-full rounded-lg border p-2 focus:border-[#0095D9] focus:ring-[#0095D9]'
              />
              {errors.email && (
                <p className='text-sm text-red-500'>{errors.email.message}</p>
              )}
            </div>

            {!loginWithOtp ? (
              <div>
                <label className='block font-medium text-gray-700'>
                  Password
                </label>
                <div className='relative'>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("password", {
                      required: "Password is required",
                    })}
                    className='mt-1 w-full rounded-lg border p-2 pr-10 focus:border-[#0095D9] focus:ring-[#0095D9]'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute inset-y-0 right-3 flex items-center text-gray-600'
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.password && (
                  <p className='text-sm text-red-500'>
                    {errors.password.message}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className='block font-medium text-gray-700'>
                  Enter OTP
                </label>
                <div className='flex space-x-2'>
                  <input
                    type='text'
                    {...register("otp", { required: "OTP is required" })}
                    className='w-full rounded-lg border p-2 focus:border-[#0095D9] focus:ring-[#0095D9]'
                  />
                  {otpTimer === 0 ? (
                    <button
                      type='button'
                      onClick={() => sendOtp()}
                      className='rounded-lg bg-[#0095D9] px-3 py-2 text-white hover:bg-[#007bbd]'
                    >
                      Send OTP
                    </button>
                  ) : (
                    <button
                      type='button'
                      className='cursor-not-allowed rounded-lg bg-gray-300 px-3 py-2 text-gray-700'
                      disabled
                    >
                      Resend OTP in {otpTimer}s
                    </button>
                  )}
                </div>
                {errors.otp && (
                  <p className='text-sm text-red-500'>{errors.otp.message}</p>
                )}
              </div>
            )}

            <div className='flex items-center justify-between text-sm text-gray-500'>
              <label className='flex items-center'>
                <input
                  type='checkbox'
                  {...register("rememberMe")}
                  className='mr-2'
                />
                Remember me for 30 days
              </label>
              <Link to='/forgot-password' className='text-[#0095D9] hover:underline'>
                Forgot password?
              </Link>
            </div>

            <div className='flex items-center text-sm text-gray-500'>
              <input
                type='checkbox'
                checked={loginWithOtp}
                onChange={() => {
                  setLoginWithOtp(!loginWithOtp);
                  setOtpSent(false);
                  setValue("otp", "");
                }}
                className='mr-2'
              />
              {loginWithOtp
                ? "Login with Password"
                : "Login with OTP instead of Password"}
            </div>

            <button
              type='submit'
              className='mt-4 w-full rounded-lg bg-[#0095D9] py-2 font-semibold text-white hover:bg-[#007bbd]'
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>

          <div className='mt-4 text-center text-sm text-gray-500'>
            Don't have an account?{" "}
            <Link to='/signup' className='text-[#0095D9] hover:underline'>
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginHfr;
